import { useCallback } from "react";
import { useIntl } from "react-intl";
import { apiGetErrorDetail } from "../helpers/api";
import useRequest from "./useRequest";

/**
 * @callback UseApiDownloadCallback
 * @param {string} url
 * @returns {void}
 */

/**
 * @typedef {Object} Props
 * @property {"noop" | "preview" | "download"} mode noop: do nothing, preview: open in new tab, download: download file
 * @property {(params: {response: import("../types/Api/ApiResponse").ApiResponse<any>, blob: Blob}) => void} [onSuccess]
 * @property {(params: {response: import("../types/Api/ApiResponse").ApiResponse<any>}) => void} [onError]
 *
 * @param {Props} props
 *
 * @returns {{download: UseApiDownloadCallback, isLoading: boolean}}
 */
function useDownloadFile({ mode, onSuccess, onError }) {
  const intl = useIntl();

  const { request, isLoading, toastSuccess, toastError } = useRequest();

  const download = useCallback(
    /**
     * @param {*} url
     * @returns
     */
    (url) => {
      request(url)
        .then(async (/** @type {any} */ response) => {
          // note: la réponse peut être soit une apiResponse, soit un fichier
          // ---
          // si esponse.data.queued est présent c'est que le fichier a été généré en asynchrone
          if (response?.data?.queued === true) {
            toastSuccess({
              title: intl.formatMessage({
                defaultMessage:
                  "Votre fichier d'export est en cours de génération. Il vous sera envoyé en pièce jointe par e-mail.",
              }),
            });
            return;
          }
          // sinon c'est que la réponse est en fait le fichier
          /** @type {Blob} */
          // @ts-ignore
          const blob = await response.blob();
          onSuccess?.({ response, blob });

          const fileName =
            // @ts-ignore
            response.headers
              .get("content-disposition")
              ?.split(";")
              ?.find((n) => n.includes("filename="))
              ?.replace("filename=", "")
              ?.replaceAll('"', "")
              ?.trim() ?? "fichier";
          const blobUrl = window.URL.createObjectURL(blob);
          if (mode === "preview") {
            window.open(blobUrl, "_blank");
          }
          if (mode === "download") {
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = fileName;
            link.style.display = "none";
            document.body.appendChild(link);
            link.addEventListener("click", () => {
              link.remove();
            });
            link.click();
          }
        })
        .catch((response) => {
          onError?.({ response });
          toastError({
            title: apiGetErrorDetail({
              error: response,
            }),
          });
        });
    },
    [intl, mode, onError, onSuccess, request, toastError, toastSuccess],
  );

  return { download, isLoading };
}

export default useDownloadFile;
